import type { Camera, Size } from './camera';
import { TOP_BAR, overviewAnchors, type HudInset, type OverviewAnchors } from './mapHud';
import { PANEL_LINE, SCENES_TOP, STRIP_H, STRIP_W, STRIP_WORLDS } from './overview';

/**
 * The strip's camera: the painting is only ever panned along x. Its zoom is set by the screen (a layout), not by the player; the one
 * exception is the glide into a world and back. Pure functions, so the layout, the stops, the clamp and the snap are tested without a
 * browser. `zoom` is screen pixels per master pixel, `x` and `y` the screen position of the strip's top-left corner.
 */

/** The most master pixels the screen shows across, however wide it is: a bigger screen draws the scenes bigger instead. About 9 tile
 *  columns, so a view's tiles (with the seam's extra column) stay under the strip's tile cap and a wide screen never drops to the
 *  blurrier level. */
export const MAX_SPAN = 4500;

/** How many master pixels the screen shows across: about 1120 on a phone, 2828 on a desktop, tablets between, then wider with the
 *  screen up to {@link MAX_SPAN}. */
export function viewSpan(width: number): number {
  if (width <= 500) return 1120;
  if (width >= 1280) return Math.min(MAX_SPAN, 2828 + (width - 1280) * 1.2);
  return 1120 + ((width - 500) / 780) * (2828 - 1120);
}

/** How far a glide into a world zooms in. */
export const GLIDE_IN = 1.6;

export interface StripLayout {
  /** The layout zoom: the camera's zoom whenever it is at rest. */
  zoom: number;
  /** The strip's top edge on screen at rest. */
  y: number;
  /** The screen's middle line between its side insets. */
  centreX: number;
  /** The most the glide zooms to. */
  maxZoom: number;
  anchors: OverviewAnchors;
}

/**
 * Zoom from the width (see {@link viewSpan}), brought down until the scenes (from their tops to the panel's line) and the panel fit
 * between the top bar and the controls along the bottom, and never below what covers the screen's width. The strip then sits as
 * low as the top bar allows without the panel meeting the bottom controls (the middle of the two limits).
 */
export function stripLayout(view: Size, inset: HudInset): StripLayout {
  const anchors = overviewAnchors(view, inset);
  const top = inset.top + TOP_BAR;
  const usable = Math.max(1, view.w - inset.left - inset.right);
  const wide = usable / viewSpan(usable);
  const high = (anchors.panelBottom - anchors.panelH - top - 4) / (PANEL_LINE - SCENES_TOP);
  const zoom = Math.max(view.w / STRIP_W, Math.min(wide, high, 1));
  const lower = top + 4 - SCENES_TOP * zoom;
  const upper = anchors.panelBottom - anchors.panelH - PANEL_LINE * zoom;
  return {
    zoom,
    y: (lower + upper) / 2,
    centreX: inset.left + usable / 2,
    maxZoom: Math.min(1, zoom * GLIDE_IN),
    anchors,
  };
}

/** The camera's x that would centre a world's stop (before the clamp at the painting's ends). */
export function idealX(lay: StripLayout, world: number, zoom = lay.zoom): number {
  return lay.centreX - STRIP_WORLDS[world]!.stop * zoom;
}

/** Keep the strip over the screen's width: the painting's ends are the camera's. */
export function clampX(x: number, zoom: number, view: Size): number {
  return Math.min(0, Math.max(view.w - STRIP_W * zoom, x));
}

/** Every camera passes through here: x within the painting, zoom between the layout's and the glide's; at the layout zoom y is the
 *  layout's (no vertical pan), during a glide it is as given (the apron paints what the strip does not cover). */
export function clampStrip(cam: Camera, view: Size, lay: StripLayout): Camera {
  const zoom = Math.min(lay.maxZoom, Math.max(lay.zoom, cam.zoom));
  return {
    x: clampX(cam.x, zoom, view),
    y: zoom - lay.zoom < 1e-4 ? lay.y : cam.y,
    zoom,
  };
}

/** The camera at rest on a world: its stop in the middle, as far as the painting's ends allow. */
export function stopCamera(lay: StripLayout, view: Size, world: number): Camera {
  return clampStrip({ x: idealX(lay, world), y: lay.y, zoom: lay.zoom }, view, lay);
}

/** The camera zoomed on a world's scene (the glide in, and where the strip opens when back from the world). */
export function zoomedCamera(lay: StripLayout, view: Size, world: number): Camera {
  const w = STRIP_WORLDS[world]!;
  const zoom = lay.maxZoom;
  const midY = w.sprite.y + w.sprite.h * 0.5;
  return clampStrip(
    { x: view.w / 2 - w.stop * zoom, y: view.h * 0.46 - midY * zoom, zoom },
    view,
    lay,
  );
}

/** The ideal x of the first and last stops: a drag's travel (past what the painting shows at its ends nothing moves, but the choice
 *  still follows the finger). */
export function dragBounds(lay: StripLayout): { min: number; max: number } {
  return { min: idealX(lay, STRIP_WORLDS.length - 1), max: idealX(lay, 0) };
}

/** How far ahead of the finger a release looks, ms of its speed. */
export const FLING_MS = 200;
/** The most stops one fling passes. */
export const FLING_STOPS = 2;

/**
 * Where a release settles. `x` is the camera's x the drag reached (before the clamp), `v` the finger's speed in px per ms (positive:
 * to the right, the painting moving to the right, towards the west). The settle is the stop nearest where the painting would drift to
 * in {@link FLING_MS}, never more than {@link FLING_STOPS} from where the drag began.
 */
export function snapWorld(lay: StripLayout, x: number, v: number, from: number): number {
  const reach = x + v * FLING_MS;
  let best = 0;
  let bestD = Infinity;
  STRIP_WORLDS.forEach((_, i) => {
    const d = Math.abs(idealX(lay, i) - reach);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  });
  return Math.max(from - FLING_STOPS, Math.min(from + FLING_STOPS, best));
}

/** How far, screen pixels, a trackpad's sideways scroll must travel for its rest to turn to the next world. */
export const SCROLL_NUDGE = 40;

/**
 * Where a trackpad's sideways scroll settles once it rests. `x` is the camera's x it reached, `startX` where it began, `from` the world
 * chosen when it began (the one a glide was heading for, if it began mid-glide). A scroll shorter than {@link SCROLL_NUDGE} stays on
 * `from`; a longer one goes at least one world the way it went, and as far as the stop nearest `x` (as {@link snapWorld} at rest):
 * a swipe shorter than half the gap between stops still turns the page, and none ever settles the other way.
 */
export function scrollWorld(lay: StripLayout, x: number, startX: number, from: number): number {
  if (Math.abs(x - startX) < SCROLL_NUDGE) return from;
  const to = snapWorld(lay, x, 0, from);
  // The camera's x going down moves the painting west: the worlds to the east come in.
  return x < startX ? Math.max(to, stepWorld(from, 1)) : Math.min(to, stepWorld(from, -1));
}

/** After a sideways scroll settles, events this small (screen pixels) are its inertia's tail, for this long (ms) after the last one. */
export const SCROLL_TAIL_PX = 12;
export const SCROLL_TAIL_MS = 300;

/** A sideways scroll event (`dx`) at `now` is the settled scroll's tail and does nothing: small, and before `quietUntil`. */
export function scrollTail(dx: number, now: number, quietUntil: number): boolean {
  return Math.abs(dx) < SCROLL_TAIL_PX && now < quietUntil;
}

/** One stop on (1) or back (-1), stopping at the road's two ends. */
export function stepWorld(from: number, step: number): number {
  return Math.max(0, Math.min(STRIP_WORLDS.length - 1, from + step));
}

/** The finger's speed from its recent moves (`t` in ms, `x` in px), over the last `window` ms; 0 when it paused. */
export function releaseSpeed(moves: readonly { t: number; x: number }[], now: number, window = 90): number {
  const recent = moves.filter((m) => now - m.t <= window);
  if (recent.length < 2) return 0;
  const a = recent[0]!;
  const b = recent[recent.length - 1]!;
  return b.t > a.t ? (b.x - a.x) / (b.t - a.t) : 0;
}

/** A point of the strip on screen at a camera. */
export function toScreen(cam: Camera, p: readonly [number, number]): { x: number; y: number } {
  return { x: cam.x + p[0] * cam.zoom, y: cam.y + p[1] * cam.zoom };
}

/** The part of the strip on screen, master pixels. */
export function viewRect(cam: Camera, view: Size): { x: number; y: number; w: number; h: number } {
  return { x: -cam.x / cam.zoom, y: -cam.y / cam.zoom, w: view.w / cam.zoom, h: view.h / cam.zoom };
}

/** The strip's own height at a zoom (the apron covers what it does not). */
export const stripHeightAt = (zoom: number): number => STRIP_H * zoom;
